import React from "react";
import { FieldArray, useFormikContext } from "formik";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { newStepTemplate } from "../helper/WorkflowConfigTemplates";

export default function StepsSidebar({ roles = [], selectedStepIndex, onSelect }) {
  const { values, setFieldValue } = useFormikContext();
  const steps = values?.steps || [];

  const getRoleLabel = (roleId) =>
    roles.find((r) => r.id === roleId)?.displayName || "";

  const swap = (arr, i, j) => {
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };

  return (
    <FieldArray name="steps">
      {(arrayHelpers) => (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              Steps
            </Typography>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                arrayHelpers.push(newStepTemplate());
                onSelect(steps.length); // select new step
              }}
            >
              Add
            </Button>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {steps.length === 0 ? (
            <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
              No steps added yet. Click <b>Add</b> to create your first step.
            </Typography>
          ) : (
            <Stack gap={1}>
              {steps.map((s, idx) => {
                const isSelected = idx === selectedStepIndex;
                const roleLabel = getRoleLabel(s.roleId);
                const childCount = s.childSteps?.length || 0;

                return (
                  <Box
                    key={s.workflowConfigurationStepId || idx}
                    onClick={() => onSelect(idx)}
                    sx={{
                      p: 1.2,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      bgcolor: isSelected ? "action.hover" : "background.paper",
                      transition: "all 0.1s ease-in-out",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ pr: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {idx + 1}. {s.stepName?.trim() ? s.stepName : "Untitled Step"}
                        </Typography>

                        <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                          {roleLabel ? roleLabel : "No role selected"}
                          {childCount > 0 ? ` • Child: ${childCount}` : ""}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={idx === 0}
                              onClick={(e) => {
                                e.stopPropagation();
                                const updated = swap(steps, idx, idx - 1);
                                setFieldValue("steps", updated);
                                onSelect(idx - 1);
                              }}
                            >
                              <ArrowUpwardIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={idx === steps.length - 1}
                              onClick={(e) => {
                                e.stopPropagation();
                                const updated = swap(steps, idx, idx + 1);
                                setFieldValue("steps", updated);
                                onSelect(idx + 1);
                              }}
                            >
                              <ArrowDownwardIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              arrayHelpers.remove(idx);

                              // select a safe index after delete
                              const next = idx >= steps.length - 1 ? Math.max(0, idx - 1) : idx;
                              onSelect(next);
                            }}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
    </FieldArray>
  );
}
